import React, { useMemo, useState } from 'react';
import { ChartData, ChartDataset, ChartOptions } from 'chart.js';
import { Bar, Scatter } from 'react-chartjs-2';
import { DistrictStudent } from 'services/districtAdminService';
import { USAGE_METRICS } from './StudentUsageChart';

const COLORS = [
  '#e6194b',
  '#3cb44b',
  '#ffe119',
  '#0082c8',
  '#f58231',
  '#911eb4',
  '#46f0f0',
  '#f032e6',
  '#d2f53c',
  '#fabebe',
  '#008080',
  '#e6beff',
  '#aa6e28',
  '#fffac8',
  '#800000',
  '#aaffc3',
  '#808000',
  '#ffd8b1',
  '#000080',
  '#808080',
];

type EvalMetricKey =
  | 'wida_reading'
  | 'wida_writing'
  | 'wida_listening'
  | 'wida_speaking'
  | 'lexile_reading';

type UsageMetricKey = 'days_online';

type MetricOption<K extends string> = {
  accessor: K;
  display: string;
  scale: { min?: number; max?: number };
};

const USERMETADATA_METRICS: MetricOption<EvalMetricKey>[] = [
  {
    accessor: 'wida_reading',
    display: 'ESL Reading',
    scale: { min: 0, max: 6 },
  },
  {
    accessor: 'wida_writing',
    display: 'ESL Writing',
    scale: { min: 0, max: 6 },
  },
  {
    accessor: 'wida_listening',
    display: 'ESL Listening',
    scale: { min: 0, max: 6 },
  },
  {
    accessor: 'wida_speaking',
    display: 'ESL Speaking',
    scale: { min: 0, max: 6 },
  },
  {
    accessor: 'lexile_reading',
    display: 'Lexile',
    scale: { min: 0, max: 2000 },
  },
];

const PERIOD_OPTIONS = [
  { label: 'Last 30 days', value: 30 },
  { label: 'Last 90 days', value: 90 },
  { label: 'Last year', value: 365 },
];

interface StudentProgressChartProps {
  students?: DistrictStudent[];
}

const StudentProgressChart: React.FC<StudentProgressChartProps> = ({
  students,
}) => {
  const [showScatter, setShowScatter] = useState(false);
  const [metric, setMetric] = useState(USERMETADATA_METRICS[0]);
  const [usageMetric, setUsageMetric] = useState(USAGE_METRICS[0]);
  const [periodDays, setPeriodDays] = useState(PERIOD_OPTIONS[0].value);

  const studentData = useMemo(() => {
    const now = new Date();
    const startDate = new Date(now);
    startDate.setDate(now.getDate() - periodDays);

    return students
      ?.map((stu) => {
        // 1) only keep submissions with a date AND a non‐null metric
        const rawSubs = stu.evaluations
          .filter(
            (e) =>
              e.date_completed != null &&
              (metric.accessor === 'lexile_reading'
                ? e.lexile_reading != null
                : (e as any)[metric.accessor] != null),
          )
          .map((e) => ({
            date: new Date(e.date_completed!),
            value:
              metric.accessor === 'lexile_reading'
                ? e.lexile_reading!
                : parseFloat((e as any)[metric.accessor]),
          }))
          .filter((p) => !isNaN(p.value))
          .sort((a, b) => a.date.getTime() - b.date.getTime());

        // 2) need at least two valid points
        if (rawSubs.length < 2) return null;

        // 3) find baseline (last ≤ startDate, or earliest after)
        const before = rawSubs.filter((s) => s.date <= startDate);
        const baseline = before.length ? before[before.length - 1] : rawSubs[0];

        // 4) latest = the very last entry
        const latest = rawSubs[rawSubs.length - 1];

        // 5) compute %Δ
        const pct =
          baseline.value > 0
            ? ((latest.value - baseline.value) / baseline.value) * 100
            : 0;

        return {
          user: stu.user.username,
          usage: stu.metadata[usageMetric.accessor],
          pct: Math.round(pct * 100) / 100,
        };
      })
      .filter(
        (d): d is { user: string; usage: number; pct: number } => d !== null,
      );
  }, [students, periodDays, metric, usageMetric]);

  // Bar: % improvement per student
  const barData = useMemo<ChartData<'bar'>>(
    () => ({
      labels: studentData?.map((s) => s.user),
      datasets: [
        {
          label: `% Δ ${metric.display}`,
          data: studentData?.map((s) => s.pct),
          backgroundColor: COLORS,
          borderColor: COLORS,
          borderWidth: 1,
        } as ChartDataset<'bar', number[]>,
      ],
    }),
    [studentData, metric],
  );

  const barOptions = useMemo<ChartOptions<'bar'>>(
    () => ({
      responsive: true,
      scales: {
        x: { type: 'category', title: { display: true, text: 'Student' } },
        y: {
          beginAtZero: true,
          title: { display: true, text: `% Δ ${metric.display}` },
        },
      },
    }),
    [metric],
  );

  // Scatter: usage vs. % improvement
  const scatterData = useMemo<ChartData<'scatter'> | undefined>(() => {
    if (!studentData) return;
    return {
      datasets: studentData.map((s, idx) => ({
        label: s.user,
        data: [{ x: s.usage, y: s.pct }],
        backgroundColor: COLORS[idx % COLORS.length],
        pointRadius: 5,
      })),
    };
  }, [studentData]);

  const scatterOptions = useMemo<ChartOptions<'scatter'>>(
    () => ({
      responsive: true,
      scales: {
        x: {
          type: 'linear',
          title: { display: true, text: usageMetric.display },
          min: usageMetric.scale.min,
          max: usageMetric.scale.max,
        },
        y: {
          title: { display: true, text: `% Δ ${metric.display}` },
        },
      },
    }),
    [metric, usageMetric],
  );

  return (
    <>
      {showScatter && scatterData ? (
        <Scatter data={scatterData} options={scatterOptions} />
      ) : (
        <Bar data={barData} options={barOptions} />
      )}

      <div className="row spaced" style={{ padding: '10px' }}>
        {/* Period selector */}
        <div className="row">
          <label className="label-normal">Period</label>
          <select
            value={periodDays}
            onChange={(e) => setPeriodDays(Number(e.target.value))}
          >
            {PERIOD_OPTIONS.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>
        </div>

        <div className="row">
          <label className="label-normal">Eval metric</label>
          <select
            value={metric.accessor}
            onChange={(e) =>
              setMetric(
                USERMETADATA_METRICS.find(
                  (m) => m.accessor === (e.target.value as EvalMetricKey),
                )!,
              )
            }
          >
            {USERMETADATA_METRICS.map((m) => (
              <option key={m.accessor} value={m.accessor}>
                {m.display}
              </option>
            ))}
          </select>
        </div>

        {showScatter && (
          <div className="row">
            <label className="label-normal">Usage metric</label>
            <select
              value={usageMetric.accessor}
              onChange={(e) =>
                setUsageMetric(
                  USAGE_METRICS.find(
                    (u) => u.accessor === (e.target.value as UsageMetricKey),
                  )!,
                )
              }
            >
              {USAGE_METRICS.map((u) => (
                <option key={u.accessor} value={u.accessor}>
                  {u.display}
                </option>
              ))}
            </select>
          </div>
        )}

        <label className="label-normal">
          <input
            type="checkbox"
            checked={showScatter}
            onChange={(e) => setShowScatter(e.target.checked)}
            style={{ marginRight: 8 }}
          />
          Show correlation
        </label>
      </div>
    </>
  );
};

export default StudentProgressChart;
